/**
 * Client-side phonetic matching aligned with api/utils/adminSearch/phoneticFr.js
 * Used only for search result highlighting (not search itself).
 */

import {
  buildSearchPhoneticExpansions,
  buildSilentFrenchSpellingVariants,
} from './frenchNameSpellingVariants.js';

const STOP_WORDS = new Set(['et', 'ou', 'non']);
const COMPACT_CODE_FUZZY_MIN_TERM_LENGTH = 5;
const COMPACT_CODE_MAX_DISTANCE = 2;
const COMPACT_CODE_FUZZY_MIN_CODE_LENGTH = 4;
const PERSON_NAME_WORD_FUZZY_MAX_DISTANCE = 2;

const normalizeSearchTerm = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const damerauLevenshteinDistance = (left, right) => {
  const a = normalizeSearchTerm(left);
  const b = normalizeSearchTerm(right);
  const alen = a.length;
  const blen = b.length;

  if (alen === 0) return blen;
  if (blen === 0) return alen;

  const matrix = Array.from({ length: alen + 1 }, () => Array(blen + 1).fill(0));

  for (let i = 0; i <= alen; i += 1) matrix[i][0] = i;
  for (let j = 0; j <= blen; j += 1) matrix[0][j] = j;

  for (let i = 1; i <= alen; i += 1) {
    for (let j = 1; j <= blen; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,
        matrix[i][j - 1] + 1,
        matrix[i - 1][j - 1] + cost
      );

      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        matrix[i][j] = Math.min(matrix[i][j], matrix[i - 2][j - 2] + cost);
      }
    }
  }

  return matrix[alen][blen];
};

const unique = (values) => [...new Set(values.filter(Boolean))];

const normalizePhoneticInput = (value) =>
  normalizeSearchTerm(
    String(value || '')
      .replace(/[\u00A0\t\r\n]+/g, ' ')
      .replace(/[’`´]/g, "'")
      .replace(/[œŒ]/g, 'oe')
      .replace(/[æÆ]/g, 'ae')
  );

const tokenizeNameField = (value) =>
  normalizePhoneticInput(value)
    .split(/[\s'-]+/)
    .map((term) => term.trim())
    .filter((term) => term && !STOP_WORDS.has(term));

const normalizeFrenchSound = (value) => {
  let word = normalizePhoneticInput(value);

  word = word
    .replace(/ph/g, 'f')
    .replace(/qu/g, 'k')
    .replace(/gn/g, 'n')
    .replace(/eau/g, 'o')
    .replace(/au/g, 'o')
    .replace(/(an|am|en|em)/g, 'an')
    .replace(/ch/g, 'sh')
    .replace(/c(?=[eiy])/g, 's')
    .replace(/c/g, 'k')
    .replace(/h/g, '')
    .replace(/([a-z])\1+/g, '$1')
    .replace(/[tdsxzp]+$/g, '')
    .replace(/e+$/g, '');

  return word;
};

const buildCompactCode = (word) => {
  const normalized = normalizeFrenchSound(word);
  if (!normalized) return '';

  const withoutVowels = normalized.replace(/[aeiouy]+/g, '');
  return (withoutVowels || normalized).toUpperCase();
};

const buildPersonCompactCodes = (word) => {
  const primary = buildCompactCode(word);
  if (!primary) return [];

  return unique([
    primary,
    primary.length > 4 ? primary.slice(0, 4) : '',
    primary.endsWith('N') ? primary.replace(/N$/, '') : '',
  ]);
};

const buildSilentFrenchSVariants = (word) =>
  buildSilentFrenchSpellingVariants(word, normalizePhoneticInput);

const encodePersonPhonetic = (word) => {
  const normalized = normalizePhoneticInput(word);
  const spellingVariants = unique(
    [
      normalized,
      ...buildSilentFrenchSpellingVariants(normalized, normalizePhoneticInput),
      ...buildSearchPhoneticExpansions(normalized, normalizePhoneticInput),
    ].filter(Boolean)
  );
  const allCodes = spellingVariants.flatMap(buildPersonCompactCodes);
  const primary = buildCompactCode(normalized);

  if (!primary) return { primary: '', alternates: [] };

  return {
    primary,
    alternates: unique(allCodes).filter((code) => code !== primary),
  };
};

const getCodesFromEncodedWord = (encoded) => {
  if (!encoded) return [];
  if (typeof encoded === 'string') return [encoded];
  return unique([encoded.primary, ...(encoded.alternates || [])]);
};

const encodePersonPhoneticCodes = (word) =>
  getCodesFromEncodedWord(encodePersonPhonetic(word));

const encodeCompanyPhoneticWord = (word) => {
  const personCodes = encodePersonPhoneticCodes(word);
  const normalized = normalizeFrenchSound(word).toUpperCase();
  const compact = normalized.replace(/[AEIOUY]+/g, '');

  return unique([
    ...personCodes,
    normalized,
    compact,
    compact.length >= 4 ? compact.slice(0, 4) : '',
    normalized.length >= 5 ? normalized.slice(0, 5) : '',
  ]);
};

const compactCodesMatchWithTolerance = (leftCode, rightCode, maxDistance) => {
  if (!leftCode || !rightCode) return false;
  if (leftCode === rightCode) return true;
  if (!maxDistance) return false;
  if (
    leftCode.length < COMPACT_CODE_FUZZY_MIN_CODE_LENGTH ||
    rightCode.length < COMPACT_CODE_FUZZY_MIN_CODE_LENGTH
  ) {
    return false;
  }

  return damerauLevenshteinDistance(leftCode, rightCode) <= maxDistance;
};

const resolveCompactCodeFuzzyDistance = (searchTerm) =>
  String(searchTerm || '').length >= COMPACT_CODE_FUZZY_MIN_TERM_LENGTH
    ? COMPACT_CODE_MAX_DISTANCE
    : 0;

const phoneticWordMatches = (searchWordOrCode, storedPhoneticWords = []) => {
  const searchTerm = Array.isArray(searchWordOrCode)
    ? ''
    : String(searchWordOrCode || '');
  const searchCodes = Array.isArray(searchWordOrCode)
    ? searchWordOrCode
    : encodePersonPhoneticCodes(searchWordOrCode);
  const storedCodes = [...new Set(storedPhoneticWords || [])];

  if (searchCodes.some((code) => storedCodes.includes(code))) return true;

  const maxDistance = resolveCompactCodeFuzzyDistance(searchTerm);
  if (!maxDistance) return false;

  return searchCodes.some((searchCode) =>
    storedCodes.some((storedCode) =>
      compactCodesMatchWithTolerance(searchCode, storedCode, maxDistance)
    )
  );
};

const resolvePersonNameSoundMaxDistance = (term) => {
  const normalizedTerm = normalizePhoneticInput(term);
  if (normalizedTerm.length >= 5) return 1;
  if (normalizedTerm.length >= 4) return 2;
  return 1;
};

const personNameWordMatchesSearchTerm = (term, word) => {
  if (!term || !word) return false;

  const normalizedTerm = normalizePhoneticInput(term);
  const normalizedWord = normalizePhoneticInput(word);
  if (!normalizedTerm || !normalizedWord) return false;

  const soundDist = damerauLevenshteinDistance(
    normalizeFrenchSound(term),
    normalizeFrenchSound(word)
  );
  const wordDist = damerauLevenshteinDistance(normalizedWord, normalizedTerm);
  const maxSoundDist = resolvePersonNameSoundMaxDistance(term);

  if (wordDist > PERSON_NAME_WORD_FUZZY_MAX_DISTANCE) return false;

  if (soundDist <= maxSoundDist) {
    if (
      soundDist === 1 &&
      normalizedTerm.length >= 5 &&
      normalizedWord.length <= normalizedTerm.length - 1
    ) {
      return wordDist === 0 || soundDist === 0;
    }
    return true;
  }

  if (
    phoneticWordMatches(term, encodePersonPhoneticCodes(word)) &&
    wordDist <= 1
  ) {
    return true;
  }

  return (
    phoneticWordMatches(term, encodePersonPhoneticCodes(word)) &&
    wordDist <= PERSON_NAME_WORD_FUZZY_MAX_DISTANCE &&
    soundDist >= 2 &&
    soundDist <= PERSON_NAME_WORD_FUZZY_MAX_DISTANCE &&
    normalizedWord.length >= normalizedTerm.length
  );
};

const COMPANY_WORD_FUZZY_MAX_DISTANCE = 2;

const companyWordMatchesSearchTerm = (term, word) => {
  if (!term || !word) return false;

  const normalizedTerm = normalizePhoneticInput(term);
  const normalizedWord = normalizePhoneticInput(word);
  if (!normalizedTerm || !normalizedWord) return false;

  const wordDist = damerauLevenshteinDistance(normalizedWord, normalizedTerm);
  if (wordDist > COMPANY_WORD_FUZZY_MAX_DISTANCE) return false;

  return phoneticWordMatches(term, encodeCompanyPhoneticWord(word));
};

export {
  companyWordMatchesSearchTerm,
  encodeCompanyPhoneticWord,
  encodePersonPhoneticCodes,
  personNameWordMatchesSearchTerm,
  phoneticWordMatches,
  tokenizeNameField,
};
